import { Box, Dialog, InputBase, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import config from 'config'
import { Icon } from 'packages/eid-icons'
import { Avatar, Divider, Loader } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useAxios, useDebounce } from 'packages/core'
import { useAppState } from 'appContext'
import { useQuery } from 'react-query'

const useData = (url, data, enabled) => {
    const callApi = useAxios()
    return useQuery(
        ['SEARCH', url, ...Object.values(data)],
        () =>
            callApi({
                url,
                method: 'POST',
                data,
            }).then((data) => data.data),
        {
            enabled,
        },
    )
}
const useInputStyles = makeStyles((theme) => ({
    root: {
        height: '3.2rem',
        'label + &': {
            marginTop: theme.spacing(3),
        },
    },
    input: {
        borderRadius: '0.5rem',
        position: 'relative',
        backgroundColor: `${theme?.palette?.background?.paper} !important`,
        border: '1px solid #d8d8dd;',
        fontSize: '1.4rem',
        padding: '0.5rem 0.8rem 0.5rem 1.2rem',
        height: '2rem',
        width: '11rem',
        lineHeight: 1.25,
        boxShadow: '0 0.4rem 0.6rem -0.2rem rgba(0, 0, 0, 0.1)',
        '&:hover, &:focus': {
            borderColor: theme?.palette?.primary?.main,
            backgroundColor: theme?.palette?.common?.white,
        },
    },
    focused: {
        backgroundColor: theme?.palette?.common?.white,
    },
    error: {
        '& input, & textarea': {
            border: '1px solid #f44336 !important',
        },
    },
}))
const useModalStyles = (topPosition, rightPosition) =>
    makeStyles((theme) => ({
        root: {
            '& .MuiDialog-paper': {
                maxWidth: 'unset',
                position: 'absolute !important',
                left: `${(rightPosition - 16) / 10}rem`,
                top: `${(topPosition - 200) / 10}rem`,
                borderRadius: '0.8rem',
                boxShadow:
                    'inset 0 0 0 1px #fbfbfd, 0 0 0 0.4rem rgba(48, 127, 193, 0.05), 0 0.4rem 1.6rem 0.4rem rgba(0, 0, 0, 0.05), 0 0.8rem 3.2rem 0.4rem rgba(0, 0, 0, 0.05)',
            },
        },
        modal: {
            backgroundColor: '#fbfbfd',
            minWidth: '62.7rem',
            minHeight: '38.1rem',
            padding: '2.2rem 2.4rem 2.3rem',
            borderRadius: '0.8rem',
            '&:focus': {
                outline: 'none',
            },
        },
        modalHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            minHeight: '2rem',
        },
        closeIcon: {
            display: 'flex',
            height: '1rem',
        },

        title: {
            fontSize: '1.6rem',
            fontWeight: 'bold',
            color: theme?.palette.common.black,
        },
        searchResults: {
            width: '57.9rem',
            height: '22.4rem',
            margin: '1.6rem 0 0',
            padding: '0.95rem 0.7rem 0.6rem 0.6rem',
            borderRadius: '0.5rem',
            boxShadow: '0 0.4rem 0.6rem 0 rgba(0, 0, 0, 0.07)',
            border: 'solid 1px #d4d4d8',
            backgroundColor: theme?.palette.common.white,
            overflow: 'auto',
        },
        personOption: {
            cursor: 'pointer',
            marginBottom: '0.3rem',
            '& div': {
                color: '#919193',
            },
            '&:hover': {
                backgroundColor: '#f5f6f8',
                '& div .friendly-name-text': {
                    color: theme?.palette.primary.main,
                },
                '& div .login-text': {
                    color: '#3b454d',
                },
            },
        },
        lineGlow: {
            height: '0.1rem',
            opacity: '0.25',
            borderStyle: 'solid',
            borderWidth: '0.1rem',
            borderImageSource:
                'linear-gradient(to right, rgba(1, 174, 143, 0), #04ab93 7%, #2c83bd 91%, rgba(48, 127, 193, 0))',
            borderImageSlice: '1',
        },
    }))
const PersonOption = ({
    option,
    labelProp = 'friendlyName',
    emailProp = 'email',
    imageUrlProp = 'imageThumbUrl',
    cdnUrl,
    disableImage = false,
    onSelect,
}) => {
    const classes = useModalStyles()()
    return (
        <Box
            className={classes.personOption}
            display="flex"
            alignItems="center"
            padding="0.8rem"
        >
            {!disableImage && (
                <Box component="span" paddingRight="2rem">
                    <Avatar src={`${cdnUrl}${option[imageUrlProp]}`} />
                </Box>
            )}
            <Box
                style={{ display: 'flex' }}
                onClick={(e) => onSelect(e, option)}
            >
                <Typography
                    display="block"
                    className="friendly-name-text"
                    style={{
                        fontSize: '1.6rem',
                        fontWeight: 'normal',
                        width: '22rem',
                    }}
                    noWrap={true}
                >
                    {option[labelProp]}
                </Typography>
                <Typography
                    display="block"
                    style={{
                        fontSize: '1.2rem',
                        width: '25.6rem',
                    }}
                    noWrap={true}
                    className="login-text"
                    title={option[emailProp]}
                >
                    {option[emailProp]}
                </Typography>
            </Box>
        </Box>
    )
}
const searchFields = [
    {
        name: 'First Name',
        placeholderLocalKey: 'FirstName',
        queryParam: 'firstName',
        type: 'string',
    },
    {
        name: 'Last Name',
        placeholderLocalKey: 'LastName',
        queryParam: 'LastName',
        type: 'string',
    },
    {
        name: 'Email',
        placeholderLocalKey: 'Email',
        queryParam: 'Email',
        type: 'string',
    },
    {
        name: 'Login',
        placeholderLocalKey: 'Login',
        queryParam: 'login',
        type: 'string',
    },
]
export const AdvancePeopleFinderPost = (props) => {
    const {
        open,
        onClose,
        onChange,
        debounceInterval = 300,
        url,
        topPosition,
        rightPosition,
        queryParams,
    } = props
    const [{ headerHeight, subHeaderHeight }] = useAppState()
    const initialValues = searchFields.map((field) => {
        return { name: field.queryParam, value: '', type: field.type }
    })

    const [searchTerms, setSearchTerms] = useState(initialValues)
    const [callEndpoint, setCallEndpoint] = useState(false)
    const debouncedValue = useDebounce(searchTerms, debounceInterval)
    const { data, isLoading } = useData(
        url,
        {
            advancedSearch: debouncedValue.filter((obj) => obj.value !== ''),
            ...queryParams,
        },
        callEndpoint && open,
    )

    // )
    useEffect(() => {
        // if (debouncedValue.filter((obj) => obj.Value !== '').length) {
        setCallEndpoint(true)
        // }
    }, [debouncedValue])
    const getModalTopPosition = () => {
        const topHeadersHeight = subHeaderHeight + headerHeight
        const midOfInputFromTop = topPosition + 25
        const halfOfPopupHeight = (23.18 * 16) / 2
        if (halfOfPopupHeight <= midOfInputFromTop - topHeadersHeight) {
            return midOfInputFromTop
        } else {
            const diff =
                midOfInputFromTop - topHeadersHeight - halfOfPopupHeight
            return midOfInputFromTop + Math.abs(diff) - 25
        }
    }
    const classes = useModalStyles(getModalTopPosition(), rightPosition)()
    const inputClasses = useInputStyles()
    const { t } = useTranslation()
    const onClosePopup = () => {
        setCallEndpoint(false)
        setSearchTerms(initialValues)
        onClose()
    }

    return (
        <>
            <Dialog
                scroll={'body'}
                open={open}
                onClose={onClose}
                classes={classes}
                BackdropProps={{ style: { opacity: '0.5' } }}
            >
                <Box className={classes.modal}>
                    <Box className={classes.modalHeader}>
                        <Typography className={classes.title}>
                            {t('AdvancedPersonFinder')}
                        </Typography>
                        {/* Commenting below close icon as it is not in zeplin design but maybe required in future. 
                        Code will be removed if there's no requirement in near future */}
                        {/* <Box className={classes.closeIcon}>
                            <IconButton onClick={() => onClosePopup()}>
                                <Icon name="Close" color="#959598" />
                            </IconButton>
                        </Box> */}
                    </Box>

                    <Box
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap', // Ensures items wrap to the next line when space is limited
                            gap: '1.7rem', // Adjust the gap between input fields
                            marginTop: '2.3rem',
                        }}
                    >
                        {searchFields.map((field, i) => {
                            return (
                                <>
                                    <InputBase
                                        key={i + field.queryParam}
                                        classes={inputClasses}
                                        placeholder={t(
                                            field.placeholderLocalKey,
                                        )}
                                        style={{ marginBottom: '1.6rem' }}
                                        value={
                                            searchTerms.find(
                                                (s) =>
                                                    s.name === field.queryParam,
                                            ).value
                                        }
                                        onChange={(e) => {
                                            const newValue = e.target.value
                                            setSearchTerms((prev) =>
                                                prev.map((term) =>
                                                    term.name ===
                                                    field.queryParam
                                                        ? {
                                                              ...term,
                                                              value: newValue,
                                                          }
                                                        : term,
                                                ),
                                            )
                                        }}
                                    />
                                </>
                            )
                        })}
                    </Box>
                    <Divider color="#d8d8d8" />
                    <Box className={classes.searchResults}>
                        {isLoading && <Loader />}
                        {data?.length === 0 && !isLoading ? (
                            <Box
                                style={{
                                    margin: '0px',
                                    justifyContent: 'center',
                                    display: 'flex',
                                }}
                            >
                                {t('Common_NoDataFound')}
                            </Box>
                        ) : (
                            !isLoading &&
                            data?.map((t, i) => {
                                return (
                                    <PersonOption
                                        option={t}
                                        cdnUrl={config.BASE_EID_URL}
                                        onSelect={(_, value) => {
                                            onChange(_, value)
                                            onClosePopup()
                                        }}
                                    />
                                )
                            })
                        )}
                    </Box>
                </Box>
                <div className={classes.lineGlow}></div>
            </Dialog>
        </>
    )
}
